import { ApiQueryParams } from '@frontend/api-utils';
import { useAppSelector } from '@frontend/common';
import { ListInfo, changeFilter, listStore } from '@frontend/repository';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { SetURLSearchParams, useSearchParams } from 'react-router-dom';

import { Props } from './header-filter';

interface ViewProps {
    showModal: boolean;
    changeShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    setSearchParams: SetURLSearchParams;
    filters?: ApiQueryParams<string | number>;
}

const useHeaderFilter = <T extends object & { id: string }>(props: Props<T>): ViewProps => {
    const store = useAppSelector(useSelector, listStore);
    const list: ListInfo | undefined = store[props.listId];
    const [showModal, changeShowModal] = useState<boolean>(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [defaultValuesInitialized, setDefaultValuesInitialized] = useState<boolean>(false);
    const searchFilterInitialized = useRef<boolean>(false);
    const queryFilterInitialized = useRef<boolean>(false);

    useEffect(() => {
        if (searchFilterInitialized.current || !props.filter?.default || !list) return;
        searchFilterInitialized.current = true;
        const searchKey = props.filter.queryParamKeyOverwrite?.get('search') ?? 'search';

        props.dispatch(
            changeFilter({
                listId: props.listId,
                key: searchKey,
                value: getQueryParamValue(searchParams, searchKey)
            })
        );
    }, [list, props.filter?.default, props.listId, props.dispatch, searchParams]);

    useEffect(() => {
        if (queryFilterInitialized.current || !props.filter?.keys || !list) return;
        queryFilterInitialized.current = true;

        props.filter.keys.forEach((key) => {
            if (typeof key === 'number') return;
            const override = props.filter?.queryParamKeyOverwrite?.get(key);

            props.dispatch(
                changeFilter({
                    listId: props.listId,
                    key: override ?? key,
                    value: getQueryParamValue(searchParams, override ?? key)
                })
            );
        });
    }, [list, props.filter?.keys, props.filter?.queryParamKeyOverwrite, props.filter?.booleanKeys, props.listId, props.dispatch, searchParams]);

    useEffect(() => {
        if (!props.filter || !props.filter.defaultValues || !list || !list.count || defaultValuesInitialized) return;
        props.filter.defaultValues.forEach((value, key) => {
            props.dispatch(
                changeFilter({
                    listId: props.listId,
                    key: key,
                    value: value
                })
            );
        });
        setDefaultValuesInitialized(true);
    }, [list]);

    return {
        changeShowModal,
        showModal,
        setSearchParams,
        filters: list?.filter
    };
};

const getQueryParamValue = (searchParams: URLSearchParams, key: string): string | null => {
    const value = searchParams.get(key);
    return value && value.length > 0 ? value : null;
};

export default useHeaderFilter;
